import { useAuthUserDetails } from "@apis/users/useUsers"

const USER_ROLES = {
  AccountNumberLookup: "AccountNumberLookup",
  Admin: "Admin",
  CanResendReward: "CanResendReward",
  CasesAdmin: "CasesAdmin",
  CasesManager: "CasesManager",
  CasesMX: "CasesMX",
  CasesOps: "CasesOps",
  CasesSales: "CasesSales",
  CasesUser: "CasesUser",
  ContentManagement: "ContentManagement",
  CustomerService: "CustomerService",
  CustomerServiceManager: "CustomerServiceManager",
  Finance: "Finance",
  Marketing: "Marketing",
  Operations: "Operations",
  Payments: "Payments",
  Regulatory: "Regulatory",
  Rejects: "Rejects",
  RetentionManager: "RetentionManager",
  SalesCoordinator: "SalesCoordinator",
  SalesManager: "SalesManager",
  SalesPartnerExec: "SalesPartnerExec",
  SalesQA: "SalesQA",
  SalesRep: "SalesRep",
  OpsManager: "OpsManager",
  Winback: "Winback",
  WinbackAutoAssignment: "WinbackAutoAssignment",
} as const

type UserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES]

export const GENERAL_ROUTES_GROUP = [
  USER_ROLES.Admin,
  USER_ROLES.Operations,
  USER_ROLES.CustomerService,
  USER_ROLES.CustomerServiceManager,
]

export const ADMIN_OPS_ROUTES_GROUP = [USER_ROLES.Admin, USER_ROLES.Operations]

export const CASE_ROUTES_GROUP = [
  USER_ROLES.Admin,
  USER_ROLES.CasesAdmin,
  USER_ROLES.CasesManager,
  USER_ROLES.CasesOps,
  USER_ROLES.CasesSales,
]

const UI_PERMISSIONS: Record<string, UserRole[]> = {
  canOpenCase: [
    USER_ROLES.Admin,
    USER_ROLES.CasesAdmin,
    USER_ROLES.CasesUser,
    USER_ROLES.CasesManager,
  ],
  canAddPromo: [USER_ROLES.Admin, USER_ROLES.Operations],
  canDropAccount: [USER_ROLES.Admin, USER_ROLES.Operations],
  canActivateAccount: [USER_ROLES.Admin, USER_ROLES.Operations],
  canSeeTitanium: [USER_ROLES.Admin, USER_ROLES.Operations],
  canSeeBraze: [USER_ROLES.Admin, USER_ROLES.CustomerServiceManager],
  canCancel: [USER_ROLES.CasesAdmin, USER_ROLES.Admin],
  canUpdateAccountNumber: [USER_ROLES.CasesMX, USER_ROLES.Operations],
  canResendReward: [USER_ROLES.CanResendReward],
  roleCanCreateCustomOffer: [
    USER_ROLES.Admin,
    USER_ROLES.Operations,
    USER_ROLES.CustomerServiceManager,
  ],
  canEditCaseOwner: [
    USER_ROLES.CasesManager,
    USER_ROLES.CasesAdmin,
    USER_ROLES.Admin,
  ],
  canEditAccountStatus: [USER_ROLES.Admin, USER_ROLES.Operations],
  canSkipDuplicateAccountValidation: [
    USER_ROLES.Admin,
    USER_ROLES.OpsManager,
  ],
} as const

const isAuthorized = (
  userRoles: { name: string }[],
  authorizedRoles: readonly string[],
) => userRoles.some((role) => authorizedRoles.includes(role.name))

export function useHasPermissions() {
  const { data: userData } = useAuthUserDetails()

  const userRoles = userData?.roles ?? []

  return Object.fromEntries(
    Object.entries(UI_PERMISSIONS).map(([permissionKey, allowedRoles]) => [
      permissionKey,
      isAuthorized(userRoles, allowedRoles),
    ]),
  ) as Record<keyof typeof UI_PERMISSIONS, boolean>
}
